import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { parseEnvNumberList } from '../../../shared/utils/envParse';
import {
  getAssignedRequests,
  getUserBatches,
  getUserWorkflows,
  reqStatusFromTab,
} from '../api/qcTaskApi';
import {
  QC_TASK_ASSIGNMENTS_STALE_MS,
  QC_TASK_BATCHES_STALE_MS,
  QC_TASK_DEFAULT_PAGE_SIZE_OPTIONS,
  QC_TASK_EXCLUDED_WORKFLOW_IDS,
  QC_TASK_FALLBACK_DEFAULT_PAGE_SIZE,
  QC_TASK_WORKFLOWS_STALE_MS,
} from '../constants/qcTaskPageConstants';
import {
  buildTableFiltersFromSearchParams,
  normalizeTabFromSearchParam,
} from '../utils/qcTaskUrlUtils';

const QC_TASK_FILTER_PARAM_KEYS = [
  'language',
  'district',
  'state',
  'duration',
  'file_name',
  'assigned_to',
  'assigned_reviewer',
  'search',
];

/** Env default page size (read once per hook instance; matches `defaultPageSize` below). */
function readDefaultPageSizeFromEnv() {
  const n = Number(import.meta.env.VITE_QC_TASK_DEFAULT_PAGE_SIZE);
  return Number.isFinite(n) && n > 0 ? n : QC_TASK_FALLBACK_DEFAULT_PAGE_SIZE;
}

/**
 * Owns all state, React Query subscriptions, and URL sync for MT-QC-TASK-001.
 * Split from the page component so the UI file stays declarative and easy to scan.
 *
 * Performance: tab / workflow / batch are read from the URL in lazy `useState` initializers so the
 * first paint can enable workflows + batches + assignments queries together (parallel fetches on
 * deep links). Browser back/forward is handled by syncing from `searchParams` when the query
 * string changes.
 *
 * @returns {object} View-model consumed by `MyQcTaskPage` and child components.
 */
export function useQcTaskPageController() {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Hydrate tab + ids from the URL on the first render (not in a mount effect) so React Query
   * can enable workflows, batches, and assignments together on deep links — avoids a wasted paint
   * and serializes fewer network round-trips.
   */
  const [activeTab, setActiveTab] = useState(() =>
    normalizeTabFromSearchParam(searchParams.get('tab')),
  );
  const [workflowId, setWorkflowId] = useState(
    () => searchParams.get('workflowId') ?? '',
  );
  const [batchId, setBatchId] = useState(
    () => searchParams.get('batchId') ?? '',
  );
  /** MRT manual pagination (server-side page/limit). */
  const [pagination, setPagination] = useState(() => ({
    pageIndex: 0,
    pageSize: readDefaultPageSizeFromEnv(),
  }));

  /** MRT sorting state (server-side). */
  const [sorting, setSorting] = useState([]);

  // Backend expects `sortBy` + `sortDirection` (ASC/DESC).
  const sortBy = sorting?.[0]?.id ?? 'file_name';
  const sortDirection = sorting?.[0]?.desc ? 'DESC' : 'ASC';

  /** Page-size dropdown values from env (stable). */
  const pageSizeOptions = useMemo(
    () =>
      parseEnvNumberList(
        import.meta.env.VITE_QC_TASK_PAGE_SIZES,
        QC_TASK_DEFAULT_PAGE_SIZE_OPTIONS,
      ),
    [],
  );

  /** Default page size from env (stable). */
  const defaultPageSize = useMemo(() => readDefaultPageSizeFromEnv(), []);

  /** API `req_status` string derived from the active tab. */
  const activeReqStatus = useMemo(
    () => reqStatusFromTab(activeTab),
    [activeTab],
  );

  /** Filters mirrored in the URL for deep links. */
  const tableFilters = useMemo(
    () => buildTableFiltersFromSearchParams(searchParams),
    [searchParams],
  );

  /**
   * Updates URL search params so `tableFilters` memo (and the server query) updates.
   * MRT calls `onColumnFiltersChange`, which we translate into these API params.
   */
  const applyTableFiltersToUrl = useCallback(
    (nextFilters) => {
      const next = new URLSearchParams(searchParams);

      // Clear all known keys so removed filters actually disappear.
      QC_TASK_FILTER_PARAM_KEYS.forEach((k) => {
        next.delete(k);
      });

      Object.entries(nextFilters ?? {}).forEach(([k, v]) => {
        if (!QC_TASK_FILTER_PARAM_KEYS.includes(k)) return;
        const s = v == null ? '' : String(v).trim();
        if (!s) return;
        next.set(k, s);
      });

      // Reset to first page when filters change.
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // --- Workflows (per tab / req status; separate cache entries per tab) ---
  const {
    data: workflowsApiData = [],
    isLoading: isWorkflowLoading,
    isFetching: isWorkflowFetching,
    error: workflowsError,
  } = useQuery({
    queryKey: ['userWorkflows', activeReqStatus],
    queryFn: () => getUserWorkflows(activeReqStatus),
    enabled: Boolean(activeReqStatus),
    staleTime: QC_TASK_WORKFLOWS_STALE_MS,
    gcTime: QC_TASK_WORKFLOWS_STALE_MS * 2,
  });

  const workflowOptions = useMemo(
    () =>
      (Array.isArray(workflowsApiData) ? workflowsApiData : []).filter(
        (w) => !QC_TASK_EXCLUDED_WORKFLOW_IDS.includes(w?.workflow_id),
      ),
    [workflowsApiData],
  );

  // --- Batches for the selected workflow ---
  const {
    data: batchesApiData = [],
    isLoading: isBatchesLoading,
    isFetching: isBatchesFetching,
    isSuccess: isBatchesSuccess,
    error: batchesError,
  } = useQuery({
    queryKey: ['userBatches', workflowId, activeReqStatus],
    queryFn: () => getUserBatches({ workflowId, reqStatus: activeReqStatus }),
    enabled: Boolean(workflowId && activeReqStatus),
    staleTime: QC_TASK_BATCHES_STALE_MS,
    gcTime: QC_TASK_BATCHES_STALE_MS * 2,
  });

  /**
   * When `workflowId` is cleared (tab change), the batches query is disabled but
   * TanStack Query may still return the previous cache — force empty options.
   */
  const batchOptions = useMemo(() => {
    if (!workflowId) return [];
    return Array.isArray(batchesApiData) ? batchesApiData : [];
  }, [batchesApiData, workflowId]);

  // --- Assignment rows (manual pagination + filters) ---
  const {
    data: assignmentsPayload,
    isLoading: isAssignmentsLoading,
    isFetching: isAssignmentsFetching,
    refetch: refetchAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: [
      'assignedRequests',
      activeReqStatus,
      workflowId,
      batchId,
      pagination.pageIndex,
      pagination.pageSize,
      tableFilters,
      sortBy,
      sortDirection,
    ],
    queryFn: () =>
      getAssignedRequests({
        reqStatus: activeReqStatus,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        batchId,
        workflowId,
        filters: tableFilters,
        sortBy,
        sortDirection,
      }),
    enabled: Boolean(activeReqStatus && workflowId && batchId),
    staleTime: QC_TASK_ASSIGNMENTS_STALE_MS,
    gcTime: QC_TASK_ASSIGNMENTS_STALE_MS * 2,
  });

  const handleSortingChange = useCallback(
    (updater) => {
      setSorting((prev) =>
        typeof updater === 'function' ? updater(prev) : updater,
      );
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [setPagination],
  );

  /**
   * Browser back/forward or edited URL: align React state with the query string.
   * Depends on `urlQueryKey` (string) only so React Router `searchParams` reference churn
   * does not re-run this effect every render.
   */
  const urlQueryKey = searchParams.toString();
  useEffect(() => {
    const params = new URLSearchParams(urlQueryKey);
    const t = normalizeTabFromSearchParam(params.get('tab'));
    const w = params.get('workflowId') ?? '';
    const b = params.get('batchId') ?? '';
    setActiveTab((cur) => (cur === t ? cur : t));
    setWorkflowId((cur) => (cur === w ? cur : w));
    setBatchId((cur) => (cur === b ? cur : b));
  }, [urlQueryKey]);

  /**
   * Auto-select first workflow when the list arrives and nothing is selected,
   * and fall back the same way if the current selection (e.g. from a deep
   * link) isn't in the (possibly filtered) options list.
   */
  useEffect(() => {
    if (workflowOptions.length === 0) return;
    const isValidSelection = workflowOptions.some(
      (w) => w.workflow_id === workflowId,
    );
    if (!workflowId || !isValidSelection) {
      setWorkflowId(workflowOptions[0]?.workflow_id ?? '');
    }
  }, [workflowOptions, workflowId]);

  /** Auto-select first batch after a successful fetch when nothing is selected. */
  useEffect(() => {
    if (isBatchesSuccess && batchOptions.length > 0 && !batchId) {
      setBatchId(batchOptions[0]?.batch_id ?? '');
    }
  }, [batchOptions, isBatchesSuccess, batchId]);

  /** Changing tab, workflow, or batch always resets to the first page (skip update if already on page 0 — avoids an extra render on mount). */
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [activeTab, workflowId, batchId]);

  /** Push tab, workflow, batch, and filters back to the URL (replace, no history spam). */
  useEffect(() => {
    const next = new URLSearchParams();
    next.set('tab', activeTab);
    if (workflowId) next.set('workflowId', workflowId);
    if (batchId) next.set('batchId', batchId);
    Object.entries(tableFilters).forEach(([key, value]) => {
      if (value) next.set(key, value);
    });
    const current = searchParams.toString();
    const upcoming = next.toString();
    if (current !== upcoming) {
      setSearchParams(next, { replace: true });
    }
  }, [
    activeTab,
    workflowId,
    batchId,
    tableFilters,
    searchParams,
    setSearchParams,
  ]);

  /**
   * Tab change: clear workflow + batch so each tab refetches its own lists
   * and avoids showing another tab’s selection.
   */
  const handleTabChange = useCallback((nextTab) => {
    setActiveTab(nextTab);
    setWorkflowId('');
    setBatchId('');
  }, []);

  const handleWorkflowChange = useCallback((newValue) => {
    setWorkflowId(newValue?.workflow_id ?? '');
    setBatchId('');
  }, []);

  const handleBatchChange = useCallback((newValue) => {
    setBatchId(newValue?.batch_id ?? '');
  }, []);

  /** Assignments query is off until both ids exist — never show cached rows from another context. */
  const assignmentsQueryEnabled = Boolean(
    activeReqStatus && workflowId && batchId,
  );

  const rows = assignmentsQueryEnabled ? (assignmentsPayload?.rows ?? []) : [];
  const rowCount = assignmentsQueryEnabled
    ? (assignmentsPayload?.totalCount ?? 0)
    : 0;

  const workflowValue =
    workflowOptions.find((w) => w.workflow_id === workflowId) ?? null;
  const batchValue = batchOptions.find((b) => b.batch_id === batchId) ?? null;

  const errorMessage =
    workflowsError?.message ||
    batchesError?.message ||
    assignmentsError?.message ||
    '';

  const tableLoading =
    assignmentsQueryEnabled && (isAssignmentsLoading || isAssignmentsFetching);

  return {
    // Tab / selection (batchId needed for table remount key on parent)
    activeTab,
    workflowId,
    batchId,
    workflowValue,
    batchValue,
    workflowOptions,
    batchOptions,
    handleTabChange,
    handleWorkflowChange,
    handleBatchChange,
    // Queries
    isWorkflowLoading,
    isWorkflowFetching,
    isBatchesLoading,
    isBatchesFetching,
    // Table
    rows,
    rowCount,
    tableLoading,
    tableFilters,
    applyTableFiltersToUrl,
    pagination,
    setPagination,
    refetchAssignments,
    pageSizeOptions,
    defaultPageSize,
    errorMessage,
    sorting,
    onSortingChange: handleSortingChange,
  };
}
